import { useEffect, useRef, useState } from 'react'
import { Background, Content, PageHeader, Spinner } from '@/presentation/components'
import { Box } from '@/presentation/components/atoms/box'
import { Grid } from '@/presentation/components/atoms/grid'
import { ColorSelectionDialog } from '@/presentation/components/molecules/colorSelectionDialog'
import { useAtom } from 'jotai/react'
import { StyledTab, StyledTabs } from './styles'
import useService from '@/main/hooks/useService'
import { getCustomizationSetting, postEditBranding, putEditAccountInfo, putEditTheme } from 'services/themeServices'
import { pushNotification } from 'store/modules/notification/actions'
import successHandler from '@/main/utils/successHandler'
import { useDispatch } from 'react-redux'
import { Paper } from '@/presentation/components/atoms/paper'
import { AccountInfoEditionDialog } from '@/presentation/components/organisms/accountInfoEditionDialog'
import { updateUserStore } from 'store/modules/user/actions'
import { PlatformPersonalization } from '@/presentation/components/organisms/platformPersonalization'
import { CompanyContactsEditionDialog } from '@/presentation/components/organisms/companyContactsEditionDialog'
import { BrandingEditionDialog } from '@/presentation/components/organisms/brandingEditionDialog'
import { GlobalThemePersistentStore } from '@/main/store/globalTheme'

const TabPanel = ({ children, value, index, ...other }) => {
  return value !== index ? null : (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {children}
    </div>
  )
}

const MODAL = {
  closed: 0,
  account: 1,
  colors: 2,
  branding: 3,
  contacts: 4,
}

export const Configuration = () => {
  const dispatch = useDispatch()
  const [theme, setTheme] = useAtom(GlobalThemePersistentStore)
  const [openModal, setOpenModal] = useState(0)
  const [selectedTab, setSelectedTab] = useState(0)

  const [fetchConfigurationState, fetchConfigurationRequest] = useService(getCustomizationSetting, {
    onCompleted: (res) => {
      setTheme(JSON.parse(res.data.theme))
    },
  })
  const resaleData = fetchConfigurationState.response?.data
  const resaleContacts = resaleData?.contact

  const [lastSelectedColors, setLastSelectedColors] = useState({
    primary: theme.palette?.brand?.primary,
    secondary: theme.palette?.brand?.secondary,
  })

  const [themeEditionState, themeEditionRequest] = useService(putEditTheme, {
    onCompleted: () => {
      setLastSelectedColors({
        primary: theme.palette?.brand?.primary,
        secondary: theme.palette?.brand?.secondary,
      })
      setTheme(theme)
      dispatch(pushNotification(successHandler('Cores alteradas com sucesso!')))
    },
    onError: () => {
      handleColorChange(lastSelectedColors)
    },
  })
  //todo remover depencias do sagas e apontar
  //pra store jotai apos trocar sistema de login
  const dataForUpdate = useRef({})
  const [editAccountState, editAccountRequest] = useService(putEditAccountInfo, {
    onCompleted: () => {
      dispatch(pushNotification(successHandler('Informações alteradas com sucesso!')))
      dispatch(
        updateUserStore({
          resale: { ...dataForUpdate.current },
        }),
      )
      fetchConfigurationRequest()
    },
  })

  const [updateBrandingState, updateBrandingRequest] = useService(postEditBranding, {
    onCompleted: () => {
      dispatch(pushNotification(successHandler('Branding alterado com sucesso!')))
      location.reload()
    },
  })

  const isLoading =
    themeEditionState.loading ||
    fetchConfigurationState.loading ||
    editAccountState.loading ||
    updateBrandingState.loading

  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue)
  }

  const handleColorChange = (colors) => {
    const newTheme = { ...theme }
    newTheme.palette.brand = {
      primary: colors.primary,
      secondary: colors.secondary,
    }
    setTheme(newTheme)
  }

  useEffect(() => {
    fetchConfigurationRequest()
  }, [])
  return (
    <Content>
      <PageHeader>Configurações</PageHeader>
      <Grid container spacing='38px'>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ height: '536px' }}>
            <StyledTabs
              value={selectedTab}
              onChange={handleTabChange}
              orientation='vertical'
              aria-label='opções de configuração'
            >
              <StyledTab label='Personalização da Plataforma' disabled={isLoading} />
            </StyledTabs>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={8}>
          <Paper sx={{ padding: '32px 16px', height: '536px' }}>
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <Spinner />
              </Box>
            ) : (
              <TabPanel value={selectedTab} index={0} style={{ overflow: 'auto', height: '100%' }}>
                <PlatformPersonalization
                  companyData={{
                    contacts: resaleData?.contact || {},
                    images: {
                      header: resaleData?.images?.header?.url,
                      footer: resaleData?.images?.footer?.url,
                      login: resaleData?.images?.login?.url,
                    },
                    site: resaleData?.url,
                  }}
                  configuration={{
                    dialogsSetters: {
                      account: () => setOpenModal(MODAL.account),
                      colors: () => setOpenModal(MODAL.colors),
                      branding: () => setOpenModal(MODAL.branding),
                      contacts: () => setOpenModal(MODAL.contacts),
                    },
                    lastSelectedColors,
                  }}
                />
              </TabPanel>
            )}

            {openModal === MODAL.account ? (
              <AccountInfoEditionDialog
                open={openModal === MODAL.account}
                onClose={() => setOpenModal(MODAL.closed)}
                onSave={(data) => {
                  editAccountRequest(data)
                  dataForUpdate.current = data
                  setOpenModal(MODAL.closed)
                }}
                initialValue={{
                  digitalAccountName: resaleContacts?.digitalAccountName,
                  displayName: resaleContacts?.displayName,
                }}
              />
            ) : null}
            {openModal === MODAL.colors ? (
              <ColorSelectionDialog
                open={openModal === MODAL.colors}
                onChange={(color) => handleColorChange(color)}
                onConfirm={() => {
                  delete theme.components
                  themeEditionRequest({ palette: JSON.stringify(theme) })
                  setOpenModal(MODAL.closed)
                }}
                onClose={() => {
                  handleColorChange(lastSelectedColors)
                  setOpenModal(MODAL.closed)
                }}
              />
            ) : null}
            {openModal === MODAL.branding ? (
              <BrandingEditionDialog
                open={openModal === MODAL.branding}
                onConfirm={(data) => {
                  updateBrandingRequest(data)
                  setOpenModal(MODAL.closed)
                }}
                onClose={() => {
                  setOpenModal(MODAL.closed)
                }}
                initialValues={{
                  header: new File([''], resaleData?.images?.header?.key),
                  footer: new File([''], resaleData?.images?.footer?.key),
                  login: new File([''], resaleData?.images?.login?.key),
                }}
              />
            ) : null}
            {openModal === MODAL.contacts ? (
              <CompanyContactsEditionDialog
                open={openModal === MODAL.contacts}
                onClose={() => setOpenModal(MODAL.closed)}
                onSave={(data) => {
                  editAccountRequest(data)
                  dataForUpdate.current = data
                  setOpenModal(MODAL.closed)
                }}
                initialValue={{
                  email: resaleContacts?.email,
                  twitter: resaleContacts?.twitter,
                  instagram: resaleContacts?.instagram,
                  linkedin: resaleContacts?.linkedin,
                  phone: resaleContacts?.phone,
                }}
              />
            ) : null}
          </Paper>
        </Grid>
      </Grid>
    </Content>
  )
}
